'use client';

/**
 * The rich package fields. The editor renders them through `kind: 'custom'`:
 * a list of service rows, a currency-prefixed price and a days input
 * are not plain `text`/`select` fields. The payment block is NOT here — it is
 * the service form's `ServiceDepositField`, shared as-is.
 */

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import type { OrganizationLocation } from '@/features/organization-locations';
import { parseMajorToCents } from '@/lib/org-currency';
import { scrollWhenWheelBlocked } from '@/lib/scroll-when-wheel-blocked';
import { cn } from '@/lib/utils';
import type { OrganizationService } from '@borradh-workspace/api-client/types';
import { formatServicePrice } from '@borradh-workspace/labels';
import {
  CheckIcon,
  ChevronsUpDownIcon,
  PlusIcon,
  Trash2Icon,
} from 'lucide-react';
import { useId, useState } from 'react';

import {
  packageLabels as L,
  type PackageFormValues,
  type PackageItemDraft,
} from './package.form';

export function PackageItemsField({
  items,
  services,
  currencySymbol,
  onServiceChange,
  onQuantityChange,
  onAdd,
  onRemove,
}: {
  items: PackageItemDraft[];
  services: OrganizationService[];
  currencySymbol: string;
  onServiceChange: (index: number, serviceId: string) => void;
  onQuantityChange: (index: number, quantity: number) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
}) {
  const id = useId();
  const chosen = new Set(items.map((item) => item.serviceId).filter(Boolean));
  const canAdd = services.length > items.length;

  return (
    <Field>
      <FieldLabel id={`${id}-label`}>{L.items}</FieldLabel>
      <div
        aria-labelledby={`${id}-label`}
        className="flex flex-col gap-4 sm:gap-2"
        role="group"
      >
        {items.map((item, index) => {
          const n = index + 1;
          // A service appears once per package; hide the ones other rows hold.
          const options = services.filter(
            (service) =>
              service.id === item.serviceId || !chosen.has(service.id)
          );
          return (
            <div
              className="flex items-end gap-2"
              // Rows have no id until saved, and a blank row's service is ''.
              key={index}
            >
              <PackageServiceSelect
                label={`Service ${n}`}
                services={options}
                currencySymbol={currencySymbol}
                onValueChange={(serviceId) => onServiceChange(index, serviceId)}
                value={item.serviceId}
              />
              <div className="w-20 shrink-0 space-y-1">
                <FieldLabel className="text-xs" htmlFor={`${id}-sessions-${n}`}>
                  Sessions
                </FieldLabel>
                <Input
                  aria-label={`Sessions for service ${n}`}
                  id={`${id}-sessions-${n}`}
                  inputMode="numeric"
                  min={1}
                  max={999}
                  step={1}
                  type="number"
                  value={Number.isNaN(item.quantity) ? '' : item.quantity}
                  onChange={(event) =>
                    onQuantityChange(index, event.target.valueAsNumber)
                  }
                />
              </div>
              <Button
                aria-label={`Remove service ${n}`}
                className="shrink-0 text-muted-foreground"
                disabled={items.length === 1}
                onClick={() => onRemove(index)}
                size="icon"
                type="button"
                variant="ghost"
              >
                <Trash2Icon className="size-4" />
              </Button>
            </div>
          );
        })}
      </div>
      <FieldDescription>
        Set how many sessions of each service the package includes.
      </FieldDescription>
      <div>
        <Button
          disabled={!canAdd}
          onClick={onAdd}
          size="sm"
          type="button"
          variant="outline"
        >
          <PlusIcon className="size-4" />
          Add a service
        </Button>
      </div>
    </Field>
  );
}

function PackageServiceSelect({
  label,
  services,
  currencySymbol,
  value,
  onValueChange,
}: {
  label: string;
  services: OrganizationService[];
  currencySymbol: string;
  value: string;
  onValueChange: (serviceId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = services.find((service) => service.id === value);
  const price = (service: OrganizationService) =>
    formatServicePrice({
      priceType: service.priceType,
      priceCents: service.priceCents,
      currencySymbol,
    });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-label={label}
          aria-expanded={open}
          role="combobox"
          type="button"
          variant="outline"
          className="h-auto min-h-9 min-w-0 flex-1 justify-between font-normal"
        >
          <span className="flex min-w-0 flex-col items-start gap-0.5 text-left sm:flex-row sm:items-center sm:gap-2">
            <span
              className={cn(
                'whitespace-normal break-words sm:truncate',
                !selected && 'text-muted-foreground'
              )}
            >
              {selected?.name ?? 'Choose a service'}
            </span>
            {selected && (
              <span className="shrink-0 text-muted-foreground text-xs">
                {price(selected)}
              </span>
            )}
          </span>
          <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-72 max-w-[calc(100vw-2rem)] p-0"
      >
        <Command>
          <CommandInput
            aria-label={`Search services for ${label.toLowerCase()}`}
            placeholder="Search services..."
            className="text-base sm:text-sm"
          />
          <CommandList onWheel={scrollWhenWheelBlocked}>
            <CommandEmpty>No services found.</CommandEmpty>
            <CommandGroup>
              {services.map((service) => (
                <CommandItem
                  key={service.id}
                  value={service.id}
                  keywords={[service.name]}
                  onSelect={() => {
                    onValueChange(service.id);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn(
                      'size-4',
                      service.id === value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                    <span className="min-w-0 whitespace-normal break-words">
                      {service.name}
                    </span>
                    <span className="shrink-0 text-muted-foreground text-xs">
                      {price(service)}
                    </span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function PackagePriceField({
  currencySymbol,
  value,
  onChange,
}: {
  currencySymbol: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const id = useId();

  return (
    <Field>
      <FieldLabel htmlFor={`${id}-price`}>{L.priceRaw}</FieldLabel>
      <InputGroup>
        <InputGroupAddon>{currencySymbol}</InputGroupAddon>
        <InputGroupInput
          id={`${id}-price`}
          inputMode="decimal"
          onChange={(e) => onChange(e.target.value)}
          placeholder="0.00"
          value={value}
        />
      </InputGroup>
    </Field>
  );
}

export function PackageValidityField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const id = useId();

  return (
    <Field>
      <FieldLabel htmlFor={`${id}-validity`}>{L.validityDays}</FieldLabel>
      <InputGroup>
        <InputGroupInput
          id={`${id}-validity`}
          inputMode="numeric"
          max="3650"
          min="1"
          onChange={(e) => onChange(e.target.value)}
          placeholder="No expiry"
          step="1"
          type="number"
          value={value}
        />
        <InputGroupAddon align="inline-end">days</InputGroupAddon>
      </InputGroup>
      <FieldDescription>
        Counted from the day it’s bought. Leave blank if it never expires.
      </FieldDescription>
    </Field>
  );
}

/**
 * The package at a glance: what the customer pays against what the same
 * services cost booked one by one. A bundle priced ABOVE that is almost always
 * a typo, so it is called out rather than shown as a negative saving.
 */
export function PackageSummaryAside({
  values,
  services,
  format,
}: {
  values: PackageFormValues;
  services: OrganizationService[];
  format: (cents: number) => string;
}) {
  const byId = new Map(services.map((service) => [service.id, service]));
  const lines = values.items
    .filter((item) => item.serviceId && byId.has(item.serviceId))
    .map((item) => ({
      service: byId.get(item.serviceId) as OrganizationService,
      quantity:
        Number.isInteger(item.quantity) && item.quantity > 0
          ? item.quantity
          : 0,
    }));

  // Only a fixed price adds up; "from", free-with-variants and POA do not.
  const separatelyCents = lines.every(
    (line) =>
      line.service.priceType === 'fixed' && line.service.priceCents != null
  )
    ? lines.reduce(
        (sum, line) => sum + (line.service.priceCents ?? 0) * line.quantity,
        0
      )
    : null;
  const parsedPrice = parseMajorToCents(values.priceRaw);
  const priceCents = parsedPrice && parsedPrice > 0 ? parsedPrice : null;
  const savingCents =
    priceCents != null && separatelyCents != null
      ? separatelyCents - priceCents
      : null;
  const validity = values.validityDays.trim();
  const upFront = upFrontLabel(values, priceCents, format);

  return (
    <div className="rounded-xl border bg-card p-4">
      <h3 className="font-medium text-sm">Package summary</h3>
      {lines.length === 0 ? (
        <p className="mt-2 text-muted-foreground text-sm">
          Choose services and a price to see what customers get.
        </p>
      ) : (
        <dl className="mt-3 space-y-2.5 text-sm">
          <SummaryRow
            label="Customers pay"
            value={priceCents != null ? format(priceCents) : '—'}
            strong
          />
          <SummaryRow
            label="Booked separately"
            value={separatelyCents != null ? format(separatelyCents) : '—'}
          />
          {savingCents != null && savingCents > 0 && separatelyCents ? (
            <SummaryRow
              className="text-emerald-700 dark:text-emerald-400"
              label="They save"
              value={`${format(savingCents)} (${Math.round((savingCents / separatelyCents) * 100)}%)`}
            />
          ) : null}
          <SummaryRow
            label="Sessions"
            value={String(lines.reduce((sum, line) => sum + line.quantity, 0))}
          />
          <SummaryRow
            label="Valid for"
            value={validity ? `${validity} days` : 'No expiry'}
          />
          <SummaryRow label="Paid up front" value={upFront} />
        </dl>
      )}
      {savingCents != null && savingCents < 0 ? (
        <p className="mt-3 rounded-lg border border-amber-500/40 bg-amber-50 p-2.5 text-amber-900 text-xs dark:bg-amber-950/20 dark:text-amber-200">
          This costs {format(-savingCents)} more than booking the same services
          one by one.
        </p>
      ) : null}
      {lines.length > 0 && separatelyCents == null ? (
        <p className="mt-3 text-muted-foreground text-xs">
          Some of these services have no fixed price, so there is nothing to
          compare against.
        </p>
      ) : null}
    </div>
  );
}

function SummaryRow({
  label,
  value,
  strong,
  className,
}: {
  label: string;
  value: string;
  strong?: boolean;
  className?: string;
}) {
  return (
    <div className={cn('flex items-baseline justify-between gap-3', className)}>
      <dt className={cn(!className && 'text-muted-foreground')}>{label}</dt>
      <dd className={cn('text-right tabular-nums', strong && 'font-semibold')}>
        {value}
      </dd>
    </div>
  );
}

/** What the customer pays when they buy it, in the words the editor uses. */
function upFrontLabel(
  values: PackageFormValues,
  priceCents: number | null,
  format: (cents: number) => string
): string {
  if (!values.requiresDeposit) return 'Nothing, pays at clinic';
  if (values.paymentPolicy === 'full')
    return priceCents != null ? `${format(priceCents)} in full` : 'Full price';
  if (values.depositBasis === 'percent') {
    const percent = Number.parseInt(values.depositPercent, 10);
    return Number.isFinite(percent) ? `${percent}% deposit` : 'Clinic deposit';
  }
  const amount = parseMajorToCents(values.depositAmount);
  return amount != null && amount > 0
    ? `${format(amount)} deposit`
    : 'Clinic deposit';
}

/**
 * Whether online booking sells the package. Styled like a promotion's own
 * "Book this promotion online" switch, the other place a clinic opts a
 * catalogue entry into online booking.
 */
export function PackageSellOnlineField({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (sellOnline: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-5 rounded-2xl border border-primary/20 bg-primary/5 p-5">
      <div className="space-y-1">
        <label className="font-semibold" htmlFor={id}>
          {L.sellOnline}
        </label>
        <p className="text-sm text-muted-foreground">
          Customers can buy this package on your booking page. Buying it books
          their first visit, and it is charged on the payment terms below.
        </p>
      </div>
      <Switch
        id={id}
        aria-label={L.sellOnline}
        checked={checked}
        onCheckedChange={onChange}
      />
    </div>
  );
}

const locationName = (location: OrganizationLocation) =>
  location.name?.trim() || location.addressLine1 || 'Unnamed location';

/**
 * The branches that sell the package online. None ticked means every branch,
 * the same rule promotions follow.
 */
export function PackageBranchesField({
  locations,
  value,
  onChange,
}: {
  locations: OrganizationLocation[];
  value: string[];
  onChange: (locationIds: string[]) => void;
}) {
  if (locations.length < 2) return null;
  const toggle = (locationId: string, checked: boolean) =>
    onChange(
      checked
        ? [...value.filter((id) => id !== locationId), locationId]
        : value.filter((id) => id !== locationId)
    );
  return (
    <Field>
      <FieldLabel>{L.locationIds}</FieldLabel>
      <FieldDescription>
        {value.length === 0
          ? 'Sold at every branch. Tick branches to limit it.'
          : 'Sold only at the ticked branches.'}
      </FieldDescription>
      <div className="flex flex-col rounded-lg border">
        {locations.map((location) => (
          // biome-ignore lint/a11y/noLabelWithoutControl: label wraps a Radix Checkbox (renders a button, not a native input), so implicit association isn't statically detected
          <label
            key={location.id}
            className="flex cursor-pointer items-center gap-3 border-b px-3 py-3 last:border-b-0 hover:bg-muted/40"
          >
            <Checkbox
              checked={value.includes(location.id)}
              onCheckedChange={(checked) =>
                toggle(location.id, checked === true)
              }
              aria-label={locationName(location)}
            />
            <span className="font-medium">{locationName(location)}</span>
          </label>
        ))}
      </div>
    </Field>
  );
}
